import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../Firebase';

export default function Contact() {
  const [formType, setFormType] = useState('enquiry'); // enquiry, complaint, feedback
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const collectionName = formType === 'complaint' ? 'complaints' : (formType === 'feedback' ? 'feedbacks' : 'enquiries');
    const entryId = `${formType === 'complaint' ? 'ticket' : 'enq'}_${Date.now()}`;

    const payload = {
      id: entryId,
      fullName,
      email,
      phone,
      subject,
      description: message,
      status: 'Open',
      category: formType,
      createdAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, collectionName, entryId), payload);
    } catch (error) {
      console.warn(`Could not save ${formType} online, saving locally:`, error);
    }

    // Save locally for dashboard access (especially complaints)
    if (formType === 'complaint') {
      try {
        const stored = localStorage.getItem('sahyadri_complaints') || '[]';
        const complaintsList = JSON.parse(stored);
        complaintsList.push(payload);
        localStorage.setItem('sahyadri_complaints', JSON.stringify(complaintsList));
      } catch (e) { }
    }

    setLoading(false);
    setFormSubmitted(true);

    setTimeout(() => {
      setFormSubmitted(false);
      setFullName('');
      setEmail('');
      setPhone('');
      setSubject('');
      setMessage('');
    }, 2500);
  };

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Page Header */}
      <section
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Connect With Us</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">We are Here to Help You</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            Register formal complaints, request financial advisory assistance, or leave feedback for services improvements.
          </p>
        </div>
      </section>

      {/* Main Info Columns & Form */}
      <section className="py-24 text-navy font-sans max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* Left Details Panel */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block">Head Office Details</span>
              <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Central Office (Sangli)</h2>
              <p className="text-gray-500 text-sm sm:text-base leading-relaxed font-light">
                Our team at Sahyadri Bhavan handles credit appraisals, general treasury settlement clearing, and cyber security audits.
              </p>
            </div>

            <div className="space-y-4 text-xs sm:text-sm text-gray-700">
              <div className="flex items-start space-x-4 bg-white p-5 rounded-3xl border border-gray-200/80 shadow-sm hover:border-[#B31825] transition-colors">
                <MapPin className="h-5 w-5 text-[#B31825] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-montserrat font-extrabold text-sm text-navy">Address</h4>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed font-light">Plot No. 42, Sahyadri Bhavan, Cooperative Plaza, Sangli Road, Sangli - 415001, Maharashtra, India</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-white p-5 rounded-3xl border border-gray-200/80 shadow-sm hover:border-[#B31825] transition-colors">
                <Phone className="h-5 w-5 text-[#B31825] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-montserrat font-extrabold text-sm text-navy">Phone Numbers</h4>
                  <p className="text-xs text-gray-500 mt-1.5 font-light font-mono">+91 2162 234567 | +91 98765 43210</p>
                  <p className="text-[10px] text-gray-400 mt-1 font-mono">Toll Free Support: 1800-270-0222 (24/7 Lost Card Blockings)</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-white p-5 rounded-3xl border border-gray-200/80 shadow-sm hover:border-[#B31825] transition-colors">
                <Mail className="h-5 w-5 text-[#B31825] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-montserrat font-extrabold text-sm text-navy">Email Enquiries</h4>
                  <p className="text-xs text-gray-500 mt-1.5 font-light font-mono">info@sahyadriurban.com | support@sahyadriurban.com</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-white p-5 rounded-3xl border border-gray-200/80 shadow-sm hover:border-[#B31825] transition-colors">
                <Clock className="h-5 w-5 text-[#B31825] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-montserrat font-extrabold text-sm text-navy">Branch Timings</h4>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed font-light">Mon - Sat: 10:00 AM - 5:00 PM</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 font-mono">Closed on Sundays, 2nd/4th Saturdays & holidays</p>
                </div>
              </div>
            </div>
          </div>


          {/* Right Form Panel */}
          <div className="lg:col-span-7 bg-white border border-gray-200 rounded-3xl p-6 sm:p-10 shadow-lg">
            {/* Form category selectors */}
            <div className="flex bg-[#F8FAFC] p-1.5 rounded-2xl border border-gray-250 mb-8 justify-between gap-1 select-none">
              <button
                type="button"
                onClick={() => { setFormType('enquiry'); setFormSubmitted(false); }}
                className={`flex-1 py-3 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${formType === 'enquiry' ? 'bg-[#052046] text-white shadow-sm' : 'text-gray-500 hover:text-navy'
                  }`}
              >
                Quick Enquiry
              </button>
              <button
                type="button"
                onClick={() => { setFormType('complaint'); setFormSubmitted(false); }}
                className={`flex-1 py-3 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${formType === 'complaint' ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-500 hover:text-navy'
                  }`}
              >
                File Complaint
              </button>
              <button
                type="button"
                onClick={() => { setFormType('feedback'); setFormSubmitted(false); }}
                className={`flex-1 py-3 rounded-xl font-montserrat font-bold text-xs transition-all duration-300 ${formType === 'feedback' ? 'bg-[#00A86B] text-white shadow-sm' : 'text-gray-500 hover:text-navy'
                  }`}
              >
                Leave Feedback
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 font-sans">
              {formSubmitted ? (
                <div className="py-16 text-center space-y-4 animate-fadeIn">
                  <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl font-bold ${formType === 'complaint' ? 'bg-[#B31825]' : (formType === 'feedback' ? 'bg-[#00A86B]' : 'bg-[#052046]')
                    }`}>
                    ✓
                  </div>
                  <h4 className="text-xl font-bold text-navy uppercase tracking-wide">
                    {formType === 'complaint' ? 'Complaint Registered!' : 'Request Sent Successfully!'}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-500 max-w-sm mx-auto leading-relaxed font-light">
                    {formType === 'complaint'
                      ? 'Your support ticket has been recorded. Our grievance cell will investigate and reply via email.'
                      : 'Thank you for connecting with Sahyadri. An executive will follow up on your email or mobile lines shortly.'
                    }
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Your Name</label>
                      <input
                        type="text"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter full name"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Mobile Number</label>
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        pattern="[0-9]{10}"
                        placeholder="10-digit number"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Email ID</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. name@domain.com"
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Subject</label>
                    <input
                      type="text"
                      required
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={formType === 'complaint' ? 'e.g. Passbook printing failure / Double SMS charges' : 'e.g. FD deposit query'}
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Description / Message</label>
                    <textarea
                      rows="4"
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={formType === 'complaint' ? 'Provide details, transaction IDs, branch details...' : 'Describe your enquiry or suggestions...'}
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 text-white font-bold rounded-xl hover:opacity-95 transition-opacity flex items-center justify-center space-x-2 shadow-lg uppercase text-xs tracking-wider ${formType === 'complaint' ? 'bg-[#B31825] shadow-[#B31825]/10' : (formType === 'feedback' ? 'bg-[#00A86B] shadow-[#00A86B]/10' : 'bg-[#052046] shadow-[#052046]/10')
                      }`}
                  >
                    <span>{loading ? 'Submitting...' : (formType === 'complaint' ? 'Register Ticket' : 'Send Enquiry')}</span>
                    <Send className="h-4 w-4" />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="w-full h-[450px] border-t border-gray-200 overflow-hidden relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3629.7328881407943!2d74.60880407492175!3d16.84232848395482!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc123c95e3b3cd1%3A0x7350ad954996a3f0!2sDigi%20Sahyadri!5e1!3m2!1sen!2sin!4v1782730470235!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Sahyadri Urban Location Map"
        />
      </section>
    </div>
  );
}
